import { type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { mercuryRegex } from '../utils/regex';
import {
  type IFrontendUrls,
  type IPaginationResponseData,
  type IResponseData,
} from './api';

const NotificationsURL = 'api/notifications';

export enum NotificationType {
  CHAT_MESSAGE = 'CHAT_MESSAGE',
  DOWNLOAD_PDF = 'DOWNLOAD_PDF',

  QUOTE_UPLOADED = 'QUOTE_UPLOADED',
  QUOTE_DECISIONS_MADE = 'QUOTE_DECISIONS_MADE',

  CONTRIBUTIONS_SET = 'CONTRIBUTIONS_SET',

  RENEWAL_CREATION = 'RENEWAL_CREATION',
  RENEWAL_DELETE = 'RENEWAL_DELETE',
  RENEWAL_DOCUMENT_ADD = 'RENEWAL_DOCUMENT_ADD',
  RENEWAL_ADDITIONAL_DOCUMENT_ADD = 'RENEWAL_ADDITIONAL_DOCUMENT_ADD',
  RENEWAL_DOCUMENT_DELETE = 'RENEWAL_DOCUMENT_DELETE',
  RENEWAL_ADDITIONAL_DOCUMENT_DELETE = 'RENEWAL_ADDITIONAL_DOCUMENT_DELETE',
  RENEWAL_DOCUMENT_APPROVE = 'RENEWAL_DOCUMENT_APPROVE',
  RENEWAL_ADDITIONAL_DOCUMENT_APPROVE = 'RENEWAL_ADDITIONAL_DOCUMENT_APPROVE',
  RENEWAL_DOCUMENT_DECLINE = 'RENEWAL_DOCUMENT_DECLINE',
  RENEWAL_ADDITIONAL_DOCUMENT_DECLINE = 'RENEWAL_ADDITIONAL_DOCUMENT_DECLINE',
  RENEWAL_PREMIUM_CALCULATOR_ADD = 'RENEWAL_PREMIUM_CALCULATOR_ADD',
  RENEWAL_PREMIUM_CALCULATOR_DELETE = 'RENEWAL_PREMIUM_CALCULATOR_DELETE',
  RENEWAL_PREMIUM_CALCULATOR_APPROVE = 'RENEWAL_PREMIUM_CALCULATOR_APPROVE',
  RENEWAL_PREMIUM_CALCULATOR_DECLINE = 'RENEWAL_PREMIUM_CALCULATOR_DECLINE',
  RENEWAL_AWAITING_REVIEW = 'RENEWAL_AWAITING_REVIEW',
  RENEWAL_COMPLETED = 'RENEWAL_COMPLETED',

  QUOTIFY_FILE_UPLOADED = 'QUOTIFY_FILE_UPLOADED',
  QUOTIFY_FILE_PROCESSED = 'QUOTIFY_FILE_PROCESSED',
  QUOTIFY_FILE_FAILED = 'QUOTIFY_FILE_FAILED',
  QUOTIFY_PLAN_STATUS_CHANGED = 'QUOTIFY_PLAN_STATUS_CHANGED',
}

export interface INotificationDTO {
  id: number;
  accountId: number;
  createdAt: string;
  updatedAt: string;
  type: NotificationType;
  jsonSerializedTarget: string;
  read: boolean;
}

export interface IGroupedNotificationDTO {
  id: string;
  title: string;
  clientName: string;
  read: boolean;
  notifications: INotificationDTO[];
}

export declare interface IUserDTO {
  id: number;
  firstName: string;
  lastName: string;
  roles: string[];
}

export interface INotificationSerializedTarget {
  id: number;
  text: string;
  createdAt: string;
  updatedAt: string;
  jsonSerializedAccount: string;
  clientChat: {
    client: {
      id: number;
      clientName: string;
    };
  };
}

export interface IHubUrlDTO {
  url: string;
  topics: Record<string, string>;
}

export interface IRedirectNotification {
  url: {
    path: string;
    prefixPath: string;
  };
  type: string;
}

export interface IRenewalBaseSerializedDataProps {
  clientId: number;
  clientName: string;
  renewalId: number;
  renewalName: string;
  createdByUser: string;
}

export interface IRenewalCreationSerializedDataProps extends IRenewalBaseSerializedDataProps {
  fileAmount: number;
}

export interface IQuoteSerializedDataProps {
  clientId: number;
  clientName: string;
  quoteId: number;
  quoteName: string;
  triggeredAt: string;
  triggeredByUser: string;
}

export interface IRenewalAwaitingReviewSerializedDataProps extends IRenewalBaseSerializedDataProps {
  triggeredAt: string;
  triggeredByUser: string;
}

export interface IRenewalCompletedSerializedDataProps extends IRenewalBaseSerializedDataProps {
  triggeredAt: string;
  triggeredByUser: string;
}

export interface IRenewalDeletionSerializedDataProps extends Omit<
  IRenewalBaseSerializedDataProps,
  'createdByUser'
> {
  deletedByUser: string;
}

export interface IQuotifySerializedDataProps {
  clientId: number;
  clientName: string;
  workbookId: number | null;
  workbookName: string;
  folderId: number;
  deprecatedFolderId: number;
  fileCount: number;
  fileNames: string[];
  triggeredByUser: string | null;
  triggeredAt: string;
}

export interface IQuotifyPlanStatusChangedSerializedDataProps {
  clientId: number;
  clientName: string;
  workbookId: number;
  workbookName: string;
  folderId: number;
  quotePlanId: number;
  newStatus: 'NOT_REVIEWED' | 'NEEDS_REVIEW' | 'VERIFIED';
  newStatusLabel: string;
  triggeredAt: string;
}

interface IHubUrlResponse extends Pick<IHubUrlDTO, 'topics'> {}

export class NotificationService {
  private readonly api: AxiosInstance;
  protected readonly redirectNotification: IRedirectNotification[];
  protected readonly repository: 'admin' | 'client' | 'edge';
  private readonly frontendUrl: IFrontendUrls = {
    edgeRoute: 'http://localhost:5173',
    adminRoute: 'http://localhost:5174',
    clientRoute: 'http://localhost:5175',
    employeeRoute: 'http://localhost:5176',
  };

  // eslint-disable-next-line no-restricted-syntax
  constructor(
    axiosInstance: AxiosInstance,
    redirectNotification: IRedirectNotification[] = [],
    repository: 'admin' | 'client' | 'edge',
    frontendUrl?: Partial<IFrontendUrls>
  ) {
    this.api = axiosInstance;
    this.repository = repository;
    this.redirectNotification = redirectNotification;
    if (frontendUrl) {
      this.frontendUrl = { ...this.frontendUrl, ...frontendUrl };
    }
  }

  public getHubUrl = async (): Promise<IHubUrlDTO> => {
    try {
      const resp =
        await this.api.get<IResponseData<IHubUrlResponse>>(`api/hub/discovery`);

      const link = resp.headers.link as string;
      if (typeof link !== 'string') {
        return {
          url: this.frontendUrl[`${this.repository}Route`],
          topics: resp.data.data.topics,
        };
      }

      const url = link.match(mercuryRegex);
      if (!url) {
        return {
          url: this.frontendUrl[`${this.repository}Route`],
          topics: resp.data.data.topics,
        };
      }

      return {
        url: url[1],
        topics: resp.data.data.topics,
      };
    } catch (err) {
      return {
        url: this.frontendUrl[`${this.repository}Route`],
        topics: {},
      };
    }
  };

  public getNotificationsByUser = async (
    page: number,
    limit: number,
    config?: AxiosRequestConfig
  ): Promise<IPaginationResponseData<INotificationDTO[]>> => {
    const result = await this.api
      .get<IPaginationResponseData<INotificationDTO[]>>(
        `${NotificationsURL}/?page=${page}&limit=${limit}`,
        config
      )
      .then((resp) => resp.data)
      .catch((err) => {
        throw err;
      });

    return result;
  };

  public toggleNotificationReadStatus = async (
    notificationIds: number[]
  ): Promise<INotificationDTO[]> => {
    const result = await this.api
      .post<IResponseData<INotificationDTO[]>>(
        `${NotificationsURL}/status/toggle`,
        {
          notificationIds,
        }
      )
      .then((resp) => resp.data.data)
      .catch((err) => {
        throw err;
      });

    return result;
  };

  public markAllAsRead = async (
    notificationIds: number[]
  ): Promise<INotificationDTO[]> => {
    const result = await this.toggleNotificationReadStatus(notificationIds);
    return result;
  };
}
